// src/pages/MedicalSchemeCodesPage.tsx
//
// ★ PHASE 6, TASK 20 — Annex A story 31: "Medical Scheme Code Management".
//
// Unlike `WardClassesPage`/`SubsystemsPage`, the six codes this screen lists
// are Authority-furnished (P2C1 §1.1.1 plus Annex A's own NSF, unreconciled —
// ENGINEERING.md open item 4), not PRS2's own taxonomy. See
// `033_medical_scheme_code.sql`'s own header. This screen does NOT feed
// `app.entitlement_period.scheme`'s own CHECK constraint — a code "defined"
// here is administered, not automatically usable by the rule engine.
//
// ⚠ PSD only, both read and write. Export: PDF/Excel via the shared export
// kernel (P2C1 §12.52).
import { plainProse } from '../lib/plain-words'
import { CategoryBadge } from '../components/StatusBadge'
import { useState, type FormEvent } from 'react'
import { SgdsButton, SgdsInput } from '@govtechsg/sgds-web-component/react'
import { api, ApiError } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useSort, sortParams } from '../lib/useSort'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { VersionHistory } from '../components/VersionHistory'
import { FormResult } from '../components/FormResult'
import { ConfirmWrite } from '../components/ConfirmWrite'
import { BoundedField } from '../components/BoundedField'
import { DatePicker } from '../components/DatePicker'
import { boundsProblems } from '../lib/published-bounds'
import { targetValue } from '../lib/sgds-dom'
import { formatIsoDate, formatInstant } from '../lib/format'
import { EmailReportButton } from '../components/EmailReportButton'
import { ExportToolbar } from '../components/ExportToolbar'

type MedicalSchemeCodeSortKey = 'code' | 'description' | 'category' | 'coverageDetails' | 'effectiveFrom'

const COLUMN_LABELS: Readonly<Record<MedicalSchemeCodeSortKey, string>> = {
  code: 'Code',
  description: 'Description',
  category: 'Category',
  coverageDetails: 'Coverage details',
  effectiveFrom: 'Effective from',
}

/**
 * ★★ WAVE 8 (Task 3) — ONE copy of each bounded field's label: read by
 * `boundsProblems` for its messages AND by the field that renders it. Two copies
 * drift the day one of them is reworded.
 *
 * ⚠ Only fields the SERVER publishes a bound for appear here. A field the route
 * checks for presence alone is deliberately absent, and its screen therefore says
 * nothing beyond presence — see `routes/administration-field-bounds.ts`.
 */
const BOUNDED_FIELDS = {
  code: 'Code',
  description: 'Description',
  category: 'Category',
  note: 'Note (optional)',
} as const

/**
 * ★★ WAVE 8 (Task 3) REVIEW FINDING — the bounded fields that ONLY EXIST ON
 * AN ESTABLISH, and are therefore not part of a RETIRE's problem set.
 *
 * ⚠ Ticking the "Retire … instead of superseding it" box unmounts them and drops
 * them from the request body, but the React state lives on this page and survives
 * the unmount. Without naming them here, a value typed before the box was ticked
 * keeps `problems.any` true and **disables Save with no error visible anywhere** —
 * the Wave 5 failure `components/FieldError.tsx`'s header records verbatim. See
 * `lib/published-bounds.ts`'s `BoundsProblemOptions` for the full reasoning.
 *
 * ⚠ This list is the SUBMITTED half of `handleSubmit`'s own `!retireInstead &&`
 * spread below. If one changes, the other has to.
 */
const ESTABLISH_ONLY_FIELDS = ['description', 'category'] as const

type FormState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'error'; message: string }
  | { status: 'success'; message: string }

export function MedicalSchemeCodesPage() {
  // ★★★ WAVE 12 (P4) — the version history and its bi-temporal rewind. The series
  // route has answered since Phase 6 and NOTHING called it until this wave; the
  // RTM's Annex A 26-28 row carried that as a gap, because an endpoint no client
  // calls is indistinguishable from one that does not work.
  //
  // ⚠ The fetcher RESOLVES TO NULL until a code is chosen rather than calling the
  // route with an empty key: that URL is the LIST route, which would return the
  // whole catalogue into a component that renders it as one code's history.
  const [historySubject, setHistorySubject] = useState('')
  const [historyAsRecordedAt, setHistoryAsRecordedAt] = useState('')
  const history = useApi(
    () =>
      historySubject.length === 0
        ? Promise.resolve(null)
        : api.medicalSchemeCodeSeries(historySubject, historyAsRecordedAt),
    [historySubject, historyAsRecordedAt],
  )
  const { sort, onSort, interacted } = useSort<MedicalSchemeCodeSortKey>()
  const schemeCodes = useApi(() => api.medicalSchemeCodes(sortParams(sort)), [sort.column, sort.dir])

  const [code, setCode] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('')
  const [coverageDetails, setCoverageDetails] = useState('')
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [note, setNote] = useState('')
  const [retireInstead, setRetireInstead] = useState(false)
  const [form, setForm] = useState<FormState>({ status: 'idle' })

  const rows = schemeCodes.data?.schemeCodes ?? []
  const total = schemeCodes.data?.total ?? 0

  /*
    ⚠ The bounds come from the PAYLOAD (`validation`), never from this file, and
    they are checked against the value that will actually be SENT rather than the
    keystrokes in the box — see `lib/published-bounds.ts` for why both of those
    matter. `undefined` while the list is loading or refused, in which case the
    fields show nothing beyond `required` and the route refuses on its own.
  */
  const validation = schemeCodes.data?.validation
  const problems = boundsProblems(
    validation,
    {
      code: code.trim().toUpperCase(),
      description: description.trim(),
      category: category.trim(),
      note: note.trim(),
    },
    BOUNDED_FIELDS,
    // ⚠ A RETIRE sends none of the establish-only fields, so none of them can be
    // in breach — see ESTABLISH_ONLY_FIELDS above for the disabled-with-no-message
    // defect this closes.
    { notSubmitted: retireInstead ? ESTABLISH_ONLY_FIELDS : [] },
  )

  const canSubmit =
    code.trim().length > 0 &&
    effectiveFrom.length > 0 &&
    !problems.any &&
    (retireInstead || (description.trim().length > 0 && category.trim().length > 0))

  /**
   * ★★ WAVE 26 — Save FREEZES a sentence and asks; the write happens only from the
   * confirmation (`ConfirmWrite`'s header says why every irreversible write does
   * this now). The sentence is composed here, once, from the fields as they were
   * when Save was pressed — never re-derived from a form the officer may since
   * have edited. It also says what does NOT happen: this catalogue does not feed
   * the rule engine, so no bill is re-priced by a change here (this file's header).
   */
  const [confirming, setConfirming] = useState<string | null>(null)

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    const upper = code.trim().toUpperCase()
    setConfirming(
      retireInstead
        ? `Retire scheme code ${upper} from ${effectiveFrom}. No successor window opens, so the catalogue and its PDF/Excel exports list no definition for it on or after that day. No bill is re-priced: the rule engine does not read this catalogue. There is no undo. A mistake is corrected by another version.`
        : `Supersede scheme code ${upper} from ${effectiveFrom}: "${description.trim()}", ${category.trim()}. The catalogue and its PDF/Excel exports show the new definition from that day. No bill is re-priced: the rule engine does not read this catalogue. There is no undo. A mistake is corrected by another version.`,
    )
  }

  const performWrite = async () => {
    setConfirming(null)
    setForm({ status: 'submitting' })
    try {
      const outcome = (await api.supersedeMedicalSchemeCode(code.trim().toUpperCase(), {
        effectiveFrom,
        establish: !retireInstead,
        ...(note.trim().length > 0 && { note: note.trim() }),
        ...(!retireInstead && {
          description: description.trim(),
          category: category.trim(),
          coverageDetails: coverageDetails.trim().length > 0 ? coverageDetails.trim() : null,
        }),
      })) as { outcome: string }
      setForm({
        status: 'success',
        message:
          outcome.outcome === 'RETIRED'
            ? `${code.trim().toUpperCase()} retired from ${effectiveFrom}.`
            : `${code.trim().toUpperCase()} superseded from ${effectiveFrom}.`,
      })
      setCode('')
      setDescription('')
      setCategory('')
      setCoverageDetails('')
      setEffectiveFrom('')
      setNote('')
      schemeCodes.reload()
    } catch (error) {
      setForm({ status: 'error', message: error instanceof ApiError ? error.message : 'Could not save this scheme code.' })
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4 flex-wrap pt-no-print">
        <div>
          <h1 className="text-3xl mb-2">Medical scheme codes</h1>
          <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
            The medical scheme code catalogue. MSO, CCS, CPW, FAW and CWM are the schemes named in
            the requirements; NSF is also catalogued here.
            Administering a code here does not, by itself, make the rule engine or entitlement path
            recognise it. Effective-dated, PSD-only.
          </p>
        </div>
        <div className="flex gap-2">
          {/*
            ★★★ WAVE 27 — one control group, owner-directed. These answer a single
            question — "what do I want to do with this list?" — and were arranged as if
            they answered three or four. See `ExportToolbar` for the whole argument.
          */}
          <ExportToolbar
            onDownload={(format) => {
              window.location.href = api.medicalSchemeCodeExportUrl(format)
            }}
            email={<EmailReportButton path="/api/administration/medical-scheme-codes/export" />}
          />
        </div>
      </div>

      <SortAnnouncement sort={sort} interacted={interacted} busy={schemeCodes.loading} columnLabels={COLUMN_LABELS} rowCount={rows.length} />

      {schemeCodes.error !== null ? (
        <ErrorCard title="Could not load medical scheme codes" message={schemeCodes.error} onRetry={schemeCodes.reload} />
      ) : schemeCodes.initialLoading ? (
        <LoadingCard label="Loading medical scheme codes" rows={4} />
      ) : rows.length === 0 ? (
        <EmptyCard
          title="No scheme codes in scope"
          message="Either nothing is currently in force, or your active identity is not PSD: this catalogue is administered by PSD only."
        />
      ) : (
        <div className="pt-card p-0 overflow-hidden">
          <div className="pt-table-wrap" aria-busy={schemeCodes.loading}>
            <table className="pt-table">
              <caption className="sr-only">Medical scheme codes currently in force</caption>
              <thead>
                <tr>
                  <SortableTh label="Code" sortKey="code" sort={sort} onSort={onSort} />
                  <SortableTh label="Description" sortKey="description" sort={sort} onSort={onSort} />
                  <SortableTh label="Category" sortKey="category" sort={sort} onSort={onSort} />
                  <SortableTh label="Coverage details" sortKey="coverageDetails" sort={sort} onSort={onSort} />
                  <SortableTh label="Effective from" sortKey="effectiveFrom" sort={sort} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="font-semibold pt-mono">{row.code}</td>
                    <td>{plainProse(row.description)}</td>
                    <td><CategoryBadge kind="schemeCategory" value={row.category} /></td>
                    <td className="text-sm">{row.coverageDetails ?? 'None'}</td>
                    <td className="text-sm" title={formatInstant(row.recordedAt)}>
                      {formatIsoDate(row.effective.from)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-[var(--sgds-color-subtle)] px-5 py-3 border-t border-[var(--sgds-border-color-muted)] pt-no-print">
            Showing {rows.length} of {total} currently in-force scheme code{total === 1 ? '' : 's'}.{' '}
            {sort.column === null ? 'The server default order.' : `Sorted by ${COLUMN_LABELS[sort.column]} (${sort.dir}).`}
          </p>
        </div>
      )}

      {/* ★★★ WAVE 12 (P4) — the version history, below the catalogue it belongs to.
          The code picker lives in the component rather than as a column on this
          table: twelve tables of differing shapes meant twelve thead/tbody edits,
          twelve chances to break a working screen for one control. */}
      <VersionHistory
        subject={historySubject}
        subjectOptions={rows.map((row) => row.code)}
        subjectHint="The scheme code codes listed above. Sort or filter that table to reach others."
        onSubjectChange={(next) => {
          // ⚠ Changing code RESETS the rewind: an instant chosen while reading one
          // code is not a statement about another, and carrying it over would
          // silently reconstruct the new one at a forgotten instant.
          setHistoryAsRecordedAt('')
          setHistorySubject(next)
        }}
        entries={history.data?.entries ?? []}
        loading={history.loading}
        error={history.error}
        onRetry={history.reload}
        asRecordedAt={historyAsRecordedAt}
        onAsRecordedAtChange={setHistoryAsRecordedAt}
        columns={[
            { header: 'Description', cell: (entry) => entry.description },
            { header: 'Category', cell: (entry) => <CategoryBadge kind="schemeCategory" value={entry.category} /> },
        ]}
      />
      <div className="pt-card flex flex-col gap-5 pt-no-print">
        <div>
          <h2 className="text-xl mb-1">Establish or retire a scheme code</h2>
          <p className="text-sm text-[var(--sgds-body-color-subtle)]">
            Superseding is the only way a scheme code changes: there is no edit-in-place. Note: this
            catalogue does not feed the entitlement records&rsquo; own list of allowed schemes, so a new code defined
            here is administered, not automatically computable.
          </p>
        </div>
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={handleSubmit}>
          <BoundedField
            label={BOUNDED_FIELDS.code}
            example="e.g. MSO, CCS, CPW, FAW, CWM, NSF."
            value={code}
            onValueChange={setCode}
            bounds={validation?.code}
            problem={problems.of.code}
            required
          />
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-semibold">
              Effective from <span aria-hidden="true">*</span>
              <span className="sr-only">required</span>
            </span>
            <DatePicker value={effectiveFrom} onChange={setEffectiveFrom} required />
          </label>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input type="checkbox" checked={retireInstead} onChange={(e) => setRetireInstead(e.target.checked)} />
            Retire this code instead of superseding it (no successor window opens)
          </label>
          {!retireInstead && (
            <>
              <BoundedField
                label={BOUNDED_FIELDS.description}
                value={description}
                onValueChange={setDescription}
                bounds={validation?.description}
                problem={problems.of.description}
                required
              />
              <BoundedField
                label={BOUNDED_FIELDS.category}
                value={category}
                onValueChange={setCategory}
                bounds={validation?.category}
                problem={problems.of.category}
                required
              />
              <div className="sm:col-span-2">
                <SgdsInput placeholder=""
                  label="Coverage details (optional)"
                  value={coverageDetails}
                  onSgdsInput={(e) => setCoverageDetails(targetValue(e))}
                />
              </div>
            </>
          )}
          <BoundedField
            className="sm:col-span-2"
            label={BOUNDED_FIELDS.note}
            value={note}
            onValueChange={setNote}
            bounds={validation?.note}
            problem={problems.of.note}
            multiline
          />
          {confirming !== null && (
            <ConfirmWrite
              summary={confirming}
              confirmLabel="Yes, apply it"
              onConfirm={() => void performWrite()}
              onCancel={() => setConfirming(null)}
            />
          )}
          <FormResult state={form} className="sm:col-span-2" />
          <div className="sm:col-span-2">
            <SgdsButton type="submit" variant="primary" disabled={!canSubmit || confirming !== null} loading={form.status === 'submitting'}>
              {retireInstead ? 'Retire' : 'Save'}
            </SgdsButton>
          </div>
        </form>
      </div>
    </div>
  )
}
